// src/pages/RenewalTemplatesPage.js — edit the email + letter body templates.
// [marker: renewals-tracker v1] also the reminder (follow-up) email and the envelope layout.
// Letter body is injected into the uploaded letterhead's empty body at send time.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillResize from 'quill-resize-module';
import 'quill-resize-module/dist/resize.css';

// [marker: rich-renewal-templates v1]
// Same editor configuration as Email Blasts: bold/italic/underline/strike,
// colour, lists, alignment, links, and images that can be dragged to resize.
// Preserve image width/height/style so drag-resize survives save -> reload -> send.
const _BaseImage = Quill.import('formats/image');
const _IMG_ATTRS = ['alt', 'height', 'width', 'style'];
class _ResizableImage extends _BaseImage {
  static formats(domNode) {
    return _IMG_ATTRS.reduce((acc, a) => {
      if (domNode.hasAttribute(a)) acc[a] = domNode.getAttribute(a);
      return acc;
    }, {});
  }
  format(name, value) {
    if (_IMG_ATTRS.indexOf(name) > -1) {
      if (value) this.domNode.setAttribute(name, value);
      else this.domNode.removeAttribute(name);
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(_ResizableImage, true);
Quill.register('modules/resize', QuillResize);

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
  resize: { locale: {}, modules: ['Resize', 'DisplaySize'] },
};
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align',
  'link', 'image', 'width', 'height',
];

// Sample values so the preview shows a realistic letter instead of raw tokens.
const PREVIEW_VARS = {
  today: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
  renewal_month: 'July', renewal_year: String(new Date().getFullYear() + 1),
  org_name: 'New Jersey Jewish Business Alliance',
  first_name: 'Sarah', last_name: 'Klein', full_name: 'Sarah Klein',
  salutation: 'Ms.', salutation_lastname: 'Ms. Klein',
  business_name: 'Klein & Associates',
  email: 'sarah@kleinassoc.com',
  address: '120 Market Street\nSuite 400\nNewark, NJ 07102',
};

function fillTokens(html) {
  let out = String(html || '');
  Object.entries(PREVIEW_VARS).forEach(([k, v]) => {
    out = out.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'gi'),
      String(v).replace(/\n/g, '<br>'));
  });
  // Anything still unfilled: show it dimmed rather than as a raw token.
  out = out.replace(/\{\{\s*([^}]+?)\s*\}\}/g,
    '<span style="background:#fef3c7;color:#92400e;padding:0 3px;border-radius:3px">$1</span>');
  return out;
}

const SYSTEM_TOKENS = [
  { k: 'today',         d: "Today's date (e.g., July 15, 2027)" },
  { k: 'renewal_month', d: "Batch's renewal month" },
  { k: 'renewal_year',  d: "Batch's renewal year" },
  { k: 'org_name',      d: 'NJJBA name' },
];

const NORMALIZED_TOKENS = [
  { k: 'first_name',     d: "Recipient's first name" },
  { k: 'last_name',      d: "Recipient's last name" },
  { k: 'full_name',      d: 'First + last' },
  { k: 'salutation',     d: 'Mr. / Ms. / etc.' },
  { k: 'salutation_lastname', d: '"Mr. Rubin" (combined, handles empty salutation)' },
  { k: 'business_name',  d: 'Customer / business name' },
  { k: 'email',          d: 'Recipient email' },
  { k: 'address',        d: 'Multi-line address block' },
];

const QB_EXCEL_TOKENS = [
  { k: 'Customer',       d: 'Business name (QB column)' },
  { k: 'First Name',     d: 'QB column' },
  { k: 'Last Name',      d: 'QB column' },
  { k: 'Mr./Ms./...',    d: 'Prefix from QB' },
  { k: 'Street1',        d: 'QB column' },
  { k: 'Street2',        d: 'QB column' },
  { k: 'City',           d: 'QB column' },
  { k: 'State',          d: 'QB column' },
  { k: 'Zip',            d: 'QB column' },
  { k: 'Main Email',     d: 'QB column' },
  { k: 'Main Phone',     d: 'QB column' },
  { k: 'Customer Type',  d: 'e.g., Executive Member' },
  { k: 'Terms',          d: 'Renewal month from QB' },
];

export default function RenewalTemplatesPage() {
  const { isManagerOrAbove } = useAuth();
  const [tpl, setTpl] = useState(null);
  const [preview, setPreview] = useState(null); /* [marker: rich-renewal-templates v1] */
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/renewals/templates')
      .then(res => setTpl(res.data))
      .catch(() => toast.error('Could not load templates.'));
  }, []);

  const update = (k, v) => { setTpl(prev => ({ ...prev, [k]: v })); setDirty(true); };
  const updateEnv = (k, v) => { setTpl(prev => ({ ...prev, envelope: { ...(prev.envelope || {}), [k]: v } })); setDirty(true); };
  const [envBusy, setEnvBusy] = useState(false);
  const previewEnvelope = async () => {
    setEnvBusy(true);
    try {
      const res = await api.post('/renewals/envelope-preview', { envelope: tpl.envelope }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_) {
      toast.error('Could not make the preview.');
    } finally {
      setEnvBusy(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.patch('/renewals/templates', tpl);
      setTpl(res.data); setDirty(false);
      toast.success('Templates saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (!tpl) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div>
      <Link to="/renewals" style={{ fontSize: 14, color: 'var(--gray-600)', textDecoration: 'none' }}>← Back to renewals</Link>

      <div className="page-header" style={{ marginTop: 8 }}>
        <div>
          <h1 className="page-title">Renewal Templates</h1>
          <p className="page-subtitle">Used for every renewal batch. Edit once, reuse forever.</p>
        </div>
        {isManagerOrAbove() && (
          <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save All Templates'}
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Available Tokens</span></div>
        <div className="card-body" style={{ padding: 14 }}>
          <p style={{ marginTop: 0, marginBottom: 12, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
            Use <code>{`{{token}}`}</code> syntax in the templates below. They're replaced per-recipient when sending.
          </p>

          <div style={{ marginBottom: 12 }}>
            <div style={sectionLabel}>System tokens</div>
            <div style={tokenGrid}>{SYSTEM_TOKENS.map(v => <Token key={v.k} v={v} />)}</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={sectionLabel}>Normalized fields (works with any Excel format)</div>
            <div style={tokenGrid}>{NORMALIZED_TOKENS.map(v => <Token key={v.k} v={v} />)}</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={sectionLabel}>Your QuickBooks Excel columns (use the exact name)</div>
            <div style={tokenGrid}>{QB_EXCEL_TOKENS.map(v => <Token key={v.k} v={v} />)}</div>
          </div>

          <div style={{
            padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 6, fontSize: '0.85rem', color: '#1e40af', lineHeight: 1.5,
          }}>
            <strong>Tip:</strong> any column header in your Excel becomes a token using its exact name.
            For example, <code>{`{{Customer Type}}`}</code> pulls the value from the "Customer Type" column.
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">📧 Email (sent to each recipient)</span></div>
        <div className="card-body">
          <div style={fg}>
            <label style={lbl}>Subject Line</label>
            <input
              type="text"
              value={tpl.emailSubject}
              onChange={e => update('emailSubject', e.target.value)}
              disabled={!isManagerOrAbove()}
              style={input}
            />
          </div>
          <div style={fg}>
            <label style={lbl}>Body</label>
            <div style={{ background: '#fff', borderRadius: 6 }}>
              <ReactQuill
                theme="snow"
                value={tpl.emailBody || ''}
                onChange={v => update('emailBody', v)}
                readOnly={!isManagerOrAbove()}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                style={{ minHeight: 260 }}
              />
            </div>
            <div style={hint}>
              Use the toolbar for bold, links, sizes, colours, lists and images (drag an image's
              corner to resize it). The letter (.docx) and statement (.pdf) attachments are added automatically.
            </div>
            <button type="button" onClick={() => setPreview({ kind: 'email', html: tpl.emailBody })}
              style={previewBtn}>👁 Preview email</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">📄 Letter Body (injected into your letterhead)</span></div>
        <div className="card-body">
          <div style={{
            padding: 12, background: '#fef3c7', border: '1px solid #fde68a',
            borderRadius: 6, fontSize: '0.85rem', color: '#78350f', marginBottom: 12, lineHeight: 1.5,
          }}>
            <strong>How this works:</strong> Upload your NJJBA letterhead .docx (with logo, board sidebar, etc.)
            with an empty body. The text you write here gets inserted into that empty body when generating each letter.
            You don't need to edit the .docx itself — just keep your branding there.
            <br /><br />
            <em>If you leave this blank,</em> the letterhead is used as-is (useful if you already embedded the body text + tokens in the .docx).
          </div>

          <div style={fg}>
            <label style={lbl}>Letter Body</label>
            <div style={{ background: '#fff', borderRadius: 6 }}>
              <ReactQuill
                theme="snow"
                value={tpl.letterBody || ''}
                onChange={v => update('letterBody', v)}
                readOnly={!isManagerOrAbove()}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                style={{ minHeight: 380 }}
              />
            </div>
            <div style={hint}>
              Formatting (bold, links, sizes, colours, lists, images) is carried into the Word letter.
              Drag an image's corner to resize it. <strong>Every letter is automatically fitted to
              exactly one page</strong> — if the body runs long, the text is shrunk to fit.
            </div>
            <button type="button" onClick={() => setPreview({ kind: 'letter', html: tpl.letterBody })}
              style={previewBtn}>👁 Preview letter</button>
          </div>
        </div>
      </div>

      {/* [marker: renewals-tracker v1] reminder email */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><span className="card-title">🔔 Reminder email (for members who haven't paid)</span></div>
        <div className="card-body">
          <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
            Used when you open a batch, pick <strong>Not paid</strong> (or <strong>Sent, no response</strong>) and click <strong>Email this group</strong>.
            You can still change the wording each time before sending.
          </p>
          <div style={fg}>
            <label style={lbl}>Subject Line</label>
            <input type="text" value={tpl.followupSubject || ''} onChange={e => update('followupSubject', e.target.value)}
              disabled={!isManagerOrAbove()} style={input} />
          </div>
          <div style={fg}>
            <label style={lbl}>Body</label>
            <div style={{ background: '#fff', borderRadius: 6 }}>
              <ReactQuill theme="snow" value={tpl.followupBody || ''} onChange={v => update('followupBody', v)}
                readOnly={!isManagerOrAbove()} modules={QUILL_MODULES} formats={QUILL_FORMATS} style={{ minHeight: 200 }} />
            </div>
            <button type="button" onClick={() => setPreview({ kind: 'email', html: tpl.followupBody })} style={previewBtn}>👁 Preview reminder</button>
          </div>
        </div>
      </div>

      {/* [marker: renewals-tracker v1] envelope */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><span className="card-title">✉️ Envelope</span></div>
        <div className="card-body">
          <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
            Used for <strong>Envelopes</strong> in every batch. Change it whenever you need, then print the envelopes again.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ ...fg, flex: '1 1 200px' }}>
              <label style={lbl}>Envelope size</label>
              <select value={tpl.envelope?.size || '10'} onChange={e => updateEnv('size', e.target.value)} disabled={!isManagerOrAbove()} style={input}>
                <option value="10">#10 business (9½ × 4⅛ in)</option>
                <option value="9">#9 (8⅞ × 3⅞ in)</option>
                <option value="a7">A7 invitation (7¼ × 5¼ in)</option>
                <option value="6x9">6 × 9 in</option>
                <option value="9x12">9 × 12 in</option>
                <option value="10x13">10 × 13 in</option>
              </select>
            </div>
            <div style={{ ...fg, flex: '0 1 140px' }}>
              <label style={lbl}>Address text size</label>
              <input type="number" min="8" max="16" value={tpl.envelope?.fontSize || 11}
                onChange={e => updateEnv('fontSize', e.target.value)} disabled={!isManagerOrAbove()} style={input} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ ...fg, flex: '1 1 260px' }}>
              <label style={lbl}>Return address (top left)</label>
              <textarea rows={4} value={tpl.envelope?.returnAddress || ''} onChange={e => updateEnv('returnAddress', e.target.value)}
                disabled={!isManagerOrAbove()} style={{ ...input, fontFamily: 'inherit' }}
                placeholder={'Leave blank to use the name and address from Settings, or type your own, e.g.\nNew Jersey Jewish Business Alliance\nPO Box …'} />
            </div>
            <div style={{ ...fg, flex: '1 1 260px' }}>
              <label style={lbl}>Recipient lines (one per line)</label>
              <textarea rows={4} value={tpl.envelope?.recipient || ''} onChange={e => updateEnv('recipient', e.target.value)}
                disabled={!isManagerOrAbove()} style={{ ...input, fontFamily: 'monospace', fontSize: '0.85rem' }} />
              <div style={hint}>Empty lines are skipped. Try {'{{salutation}} {{full_name}}'} or add “Attn: Accounts Payable”.</div>
            </div>
          </div>
          <div style={fg}>
            <label style={lbl}>Note at the bottom left (optional)</label>
            <input type="text" value={tpl.envelope?.note || ''} onChange={e => updateEnv('note', e.target.value)}
              disabled={!isManagerOrAbove()} style={input} placeholder="e.g. Membership Renewal Enclosed" />
          </div>
          <button type="button" onClick={previewEnvelope} disabled={envBusy} style={previewBtn}>
            {envBusy ? 'Making preview…' : '👁 Preview envelope (PDF)'}
          </button>
        </div>
      </div>

      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 10, width: '100%',
            maxWidth: preview.kind === 'letter' ? 850 : 700,
            maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
            }}>
              <strong>{preview.kind === 'letter' ? 'Letter preview' : 'Email preview'}</strong>
              <button onClick={() => setPreview(null)}
                style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>×</button>
            </div>
            <div style={{ padding: 12, background: '#f1f5f9', fontSize: '0.8rem', color: '#475569', borderBottom: '1px solid #e5e7eb' }}>
              Tokens are filled with sample data. {preview.kind === 'letter'
                ? 'Shown on a US-Letter page — the real letter is printed on your letterhead and auto-fitted to one page.'
                : 'Your organization header/footer is added automatically when sending.'}
            </div>
            <div style={{ overflowY: 'auto', padding: 20, background: '#e2e8f0' }}>
              <div
                style={preview.kind === 'letter' ? {
                  width: '8.5in', minHeight: '11in', margin: '0 auto', padding: '1in',
                  background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  fontFamily: 'Calibri, Arial, sans-serif', fontSize: '11pt', lineHeight: 1.15,
                  transform: 'scale(0.85)', transformOrigin: 'top center',
                } : {
                  background: '#fff', padding: 20, borderRadius: 6,
                  fontFamily: 'Arial, sans-serif', fontSize: '14px', lineHeight: 1.5,
                }}
                dangerouslySetInnerHTML={{ __html: fillTokens(preview.html) }}
              />
            </div>
          </div>
        </div>
      )}

      {dirty && (
        <div style={{
          position: 'sticky', bottom: 20, marginTop: 20, padding: 14,
          background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 600, color: '#92400e' }}>Unsaved changes.</span>
          {isManagerOrAbove() && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Token({ v }) {
  return (
    <div style={{
      padding: '6px 10px', background: 'var(--gray-50)', borderRadius: 4,
      fontSize: '0.8rem', display: 'flex', flexDirection: 'column',
    }}>
      <code style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{`{{${v.k}}}`}</code>
      <span style={{ color: 'var(--gray-600)', fontSize: '0.75rem' }}>{v.d}</span>
    </div>
  );
}

const fg = { marginBottom: 16 };
const lbl = { display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.85rem', color: 'var(--gray-700)' };
const input = { width: '100%', padding: '8px 12px', fontSize: '0.95rem', border: '1px solid var(--gray-300)', borderRadius: 6, background: '#fff', boxSizing: 'border-box' };
const previewBtn = {
  marginTop: 10, padding: '7px 14px', fontSize: '0.85rem',
  border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff',
  color: '#1a3a5c', cursor: 'pointer', fontWeight: 600,
};
const hint = { fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 3 };
const sectionLabel = { fontSize: '0.8rem', fontWeight: 700, color: 'var(--gray-700)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 };
const tokenGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 };
